import { useCallback, useEffect, useRef } from 'react';

export function useMobileSnapPanels({
  isEnabled,
  getPanelIndex,
  initialPanel = 'main',
  defaultPanel = 'main',
  eventName = 'output:activate-panel',
  onActivatePanel,
}) {
  const scrollContainerRef = useRef(null);

  const getPanelScrollLeft = useCallback(
    function getPanelScrollLeftCallback(container, panel) {
      return getPanelIndex(panel) * container.clientWidth;
    },
    [getPanelIndex],
  );

  const scrollToPanel = useCallback(
    function scrollToPanelCallback(panel) {
      const container = scrollContainerRef.current;
      if (!container) return;

      container.scrollTo({
        left: getPanelScrollLeft(container, panel),
        behavior: 'smooth',
      });
    },
    [getPanelScrollLeft],
  );

  const setScrollContainerRef = useCallback(
    function setScrollContainerRefCallback(node) {
      if (node) {
        node.scrollLeft = getPanelScrollLeft(node, initialPanel);
      }
      scrollContainerRef.current = node;
    },
    [getPanelScrollLeft, initialPanel],
  );

  const handleActivatePanel = useCallback(
    function handleActivatePanelCallback(event) {
      if (!isEnabled) return;
      const panel = event.detail?.panel || defaultPanel;

      if (onActivatePanel) onActivatePanel(panel, event);

      scrollToPanel(panel);
    },
    [defaultPanel, isEnabled, onActivatePanel, scrollToPanel],
  );

  useEffect(() => {
    window.addEventListener(eventName, handleActivatePanel);

    return () => {
      window.removeEventListener(eventName, handleActivatePanel);
    };
  }, [eventName, handleActivatePanel]);

  return {
    scrollContainerRef,
    scrollToPanel,
    setScrollContainerRef,
  };
}
